import { type Node as ProsemirrorNode } from "@tiptap/pm/model";

import { freshCodeBlockRuntimeAttrs } from "@/components/tiptap/code-block/codeBlockRuntimeAttrs";
import { pasteNodes } from "@/input/utils/editOperators";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeJson } from "@corca/data";
import type { JSONContent } from "@tiptap/core";

export type NodeIdMapping = Map<string, string>;

type GraphToRemap = { tiptapNode: JSONContent; graphId: string };

function stripCustomBlocks(content: JSONContent): JSONContent | undefined {
  if (content.type === "customBlock") {
    return undefined;
  }

  const strippedContent = content.content
    ?.map(stripCustomBlocks)
    .filter((node): node is JSONContent => Boolean(node));

  return {
    ...content,
    ...(strippedContent ? { content: strippedContent } : {}),
  };
}

export function copyTiptapContent(
  nodeToCopy: ProsemirrorNode,
  fromDoc: DocumentModel,
  toDoc: DocumentModel,
  { atPos, uploadSourceScope }: { atPos?: number; uploadSourceScope?: UploadScope } = {},
): NodeIdMapping {
  const toTiptap = toDoc.editor.tiptap;
  if (!toTiptap) {
    return new Map();
  }

  // Default to inserting at the end of the document to avoid accidentally
  // nesting copied content (e.g. lists) inside the currently focused node.
  const insertionPos = atPos ?? toTiptap.state.doc.content.size;

  const { jsonContent, nodeIdMapping } = prepareTiptapContentCopy(nodeToCopy, fromDoc, toDoc, {
    uploadSourceScope,
  });
  if (!jsonContent) {
    return nodeIdMapping;
  }

  // Unwrap a top-level doc so insertContent receives its child blocks; tiptap
  // can't insert a `doc`-typed node inside another document.
  const contentToInsert = jsonContent.type === "doc" ? (jsonContent.content ?? []) : jsonContent;
  if (Array.isArray(contentToInsert) && contentToInsert.length === 0) {
    return nodeIdMapping;
  }

  toTiptap
    .chain()
    .focus(insertionPos)
    .insertContentAndHighlight(contentToInsert, insertionPos)
    .run();

  return nodeIdMapping;
}

/**
 * Serializes `nodeToCopy` for inserting as a copy: its math, graphs and code
 * blocks are duplicated in `toDoc` and the JSON points at the new nodes, so the
 * copy doesn't share them with the original. Inserting the JSON is up to the caller.
 */
export function prepareTiptapContentCopy(
  nodeToCopy: ProsemirrorNode,
  fromDoc: DocumentModel,
  toDoc: DocumentModel,
  { uploadSourceScope }: { uploadSourceScope?: UploadScope } = {},
): { jsonContent: JSONContent | undefined; nodeIdMapping: NodeIdMapping } {
  const nodeIdMapping: NodeIdMapping = new Map();
  const toTiptap = toDoc.editor.tiptap;
  if (!toTiptap) {
    return { jsonContent: undefined, nodeIdMapping };
  }

  const nodesToCopy: NodeJson[] = [];
  const tiptapNodesToUpdate: JSONContent[] = [];
  const originalNodeIds: string[] = [];
  const graphNodesToRemap: GraphToRemap[] = [];

  const upgradeCodeBlock = (node: JSONContent) => {
    node.attrs = freshCodeBlockRuntimeAttrs(node.attrs, toTiptap.state);
  };

  // Mark copied uploads with their source scope (e.g. the chat) so the
  // target editor can re-attach the blob to its own entity.
  const stampUploadSource = (node: JSONContent) => {
    if (
      uploadSourceScope &&
      (node.type === "image" || node.type === "fileAttachment") &&
      node.attrs?.key &&
      !node.attrs.sourceUid
    ) {
      node.attrs = {
        ...node.attrs,
        sourceUid: uploadSourceScope.uid,
        sourceType: uploadSourceScope.type,
      };
    }
  };

  // Iterate over the tiptap content to copy expression nodes, generate new node ids,
  // and upgrade code blocks with execution metadata.
  const iterateTiptapNodes = (tiptapContent: JSONContent) => {
    tiptapContent.content?.forEach((node) => {
      if (node.type === "inlineExpression" && node.attrs?.nodeId) {
        const nodeId = node.attrs.nodeId as string;
        const matchedNode = fromDoc.nodes.get(nodeId)?.toJSON();
        if (matchedNode && matchedNode.id) {
          tiptapNodesToUpdate.push(node);
          nodesToCopy.push(matchedNode);
          originalNodeIds.push(nodeId);
        }
      }
      if (node.type === "graph" && node.attrs?.graphId) {
        graphNodesToRemap.push({ tiptapNode: node, graphId: node.attrs.graphId as string });
      }
      if (node.type === "codeBlock") {
        upgradeCodeBlock(node);
      }
      stampUploadSource(node);
      if (node.content) {
        iterateTiptapNodes(node);
      }
    });
  };
  const jsonContent = stripCustomBlocks(nodeToCopy.toJSON() as JSONContent);
  if (!jsonContent) {
    return { jsonContent, nodeIdMapping };
  }

  // Handle root node directly (e.g. when duplicating a single graph or code block)
  if (jsonContent.type === "graph" && jsonContent.attrs?.graphId) {
    graphNodesToRemap.push({
      tiptapNode: jsonContent,
      graphId: jsonContent.attrs.graphId as string,
    });
  }
  if (jsonContent.type === "codeBlock") {
    upgradeCodeBlock(jsonContent);
  }
  stampUploadSource(jsonContent);

  iterateTiptapNodes(jsonContent);

  // Use a proper transaction to ensure all nodes are created and synced
  toDoc.transact(() => {
    const createdNodes = pasteNodes(toDoc, nodesToCopy, {
      ignoreActiveCursor: true,
    });

    // Update tiptap node references to point to the newly created nodes
    createdNodes.forEach((createdNode, index) => {
      if (index < tiptapNodesToUpdate.length) {
        const tiptapNode = tiptapNodesToUpdate[index];
        if (tiptapNode.attrs) {
          tiptapNode.attrs = {
            ...tiptapNode.attrs,
            nodeId: createdNode.id,
          };
        }
        // Build mapping from original nodeId to new nodeId
        if (index < originalNodeIds.length && createdNode.id) {
          nodeIdMapping.set(originalNodeIds[index], createdNode.id);
        }
      }
    });

    // Duplicate graph nodes so the new block has its own graph (not shared with the original)
    graphNodesToRemap.forEach(({ tiptapNode, graphId }) => {
      const graphNode = fromDoc.nodes.get(graphId);
      if (!graphNode?.is(GraphNodeModel)) {
        return;
      }

      const newGraphNode = createNodeFrom(graphNode, toDoc, { resetIds: true });
      if (!newGraphNode) {
        return;
      }

      toDoc.textNode.appendChild(newGraphNode);
      if (tiptapNode.attrs && newGraphNode.id) {
        tiptapNode.attrs = {
          ...tiptapNode.attrs,
          graphId: newGraphNode.id,
        };
        nodeIdMapping.set(graphId, newGraphNode.id);
      }
    });
  }, "copyTiptapContent");

  return { jsonContent, nodeIdMapping };
}
